import { useOrganization } from '@clerk/react';
import { useId } from 'react';
import { toast } from 'sonner';
import { useCopilotRuntimeOptional } from '@/components/agent-chat/copilot-runtime-provider.component';
import { announceChatsChanged } from '@/components/sidebar/tabs/use-chat-lists.hook';
import { CopyButton } from '@/components/ui/copy-button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  sharedChatPath,
  useCluMultiplayerEnabled,
  useGetSharedChatQuery,
  useSetChatSharedMutation,
} from '@/store/features/clu-sharing.feature';

export interface ShareableChat {
  id: string;
  title: string;
}

/**
 * Shares a chat with the organization, or stops: shared, anyone in it opens
 * the chat and keeps chatting in it as themselves, seeing all of it. The link
 * shows while the chat is shared. Whether it is shared is the server's
 * answer, so any menu can open it; a change reads the chat lists again, which
 * mark shared chats.
 */
export function ShareChatDialog({
  chat,
  onClose,
}: {
  chat: ShareableChat;
  onClose: () => void;
}) {
  const switchId = useId();
  const { organization } = useOrganization();
  const orgName = organization?.name ?? 'your workspace';
  const multiplayer = useCluMultiplayerEnabled();
  const { data, isFetching } = useGetSharedChatQuery(chat.id);
  const [setShared, { isLoading: saving }] = useSetChatSharedMutation();
  const runtime = useCopilotRuntimeOptional();
  const isShared = data?.shared_at != null;
  const link = `${window.location.origin}${sharedChatPath(chat.id)}`;

  const toggle = async (shared: boolean) => {
    try {
      await setShared({ id: chat.id, shared }).unwrap();
      void runtime?.fetchConversations();
      announceChatsChanged();
    } catch {
      toast.error(
        shared ? "Couldn't share the chat" : "Couldn't stop sharing the chat"
      );
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='max-w-md'>
        <DialogHeader>
          <DialogTitle>Share chat</DialogTitle>
          <DialogDescription className='truncate'>
            {chat.title}
          </DialogDescription>
        </DialogHeader>
        <div className='flex items-start justify-between gap-4'>
          <div className='space-y-1'>
            <Label htmlFor={switchId}>Share with {orgName}</Label>
            <p className='text-xs text-muted-foreground'>
              {multiplayer
                ? `Anyone at ${orgName} can open this chat and keep chatting in it.`
                : `Anyone at ${orgName} with the link can read this chat and continue it in a chat of their own.`}
            </p>
          </div>
          <Switch
            id={switchId}
            checked={isShared}
            disabled={!data || isFetching || saving}
            onCheckedChange={(shared) => void toggle(shared)}
          />
        </div>
        {isShared && (
          <div className='flex items-center gap-2'>
            <Input
              readOnly
              value={link}
              aria-label='Link to this chat'
              onFocus={(event) => event.currentTarget.select()}
              className='h-8 text-[13px]'
            />
            <CopyButton value={link} label='Copy link' />
          </div>
        )}
        <p className='text-xs text-muted-foreground'>
          Teammates see your messages, Clu's replies and which tools Clu used,
          not what the tools returned. Messages you add later show up too.
        </p>
      </DialogContent>
    </Dialog>
  );
}
